import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useState} from "react";
import {ReactEditor} from "slate-react";

import {IntegerField} from "@khanacademy/event-schemas";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import Pill from "@khanacademy/wonder-blocks-pill";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Errors} from "@khanacademy/wonder-stuff-core";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {Dismissible} from "@khan/component-dismissible";
import {useCoeditorContext} from "@khan/guide-coeditor-core";
import {isIFrame, useAIGuideToolPersona} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {
    clearUnsavedChanges,
    getDocumentTitle,
    HeaderMenu,
    HeaderType,
    PowerIndicator,
    printContents,
    type AutosavingStatus,
} from "@khan/guide-ui";
import {downloadConversation} from "@khan/guide-utils";
import {useExportQuestionSetToBlooket} from "@khan/khanmigo-coediting/export";
import {
    isValidQuestionSet,
    mapBlooketQuestionsToSavableFormat,
    useUpdateBlooketMutation,
    type QuizContent,
} from "@khan/khanmigo-tools/blooket";
import {KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";

import type {AIContentTabTool} from "#class-package/content/ai-content-tab-tool-cards-helpers.ts";
import useProfile from "#data-access/use-profile.ts";
import {CANVAS_INTEGRATION_PATH} from "#guide-canvas-integration/const.ts";
import {KhanmigoForTeachersReferralInProductModal} from "#khanmigo-for-teachers-referrals-package/khanmigo-for-teachers-referral-in-product-modal.tsx";
import useTeacherIsEligibleForReferrals from "#khanmigo-for-teachers-referrals-package/use-teacher-is-eligible-for-referrals.ts";
import useWhamBoostReferralFlag from "#khanmigo-for-teachers-referrals-package/use-wham-boost-referral-flag.ts";
import {PARENT_KHANMIGO_TOOLS_PATH} from "#page-package/util.ts";

import GetShareLinkModal from "../resource-tab/get-share-link-modal.tsx";

import AIToolExportDropdown from "./ai-tool-export-dropdown.tsx";
import AIToolIcon from "./ai-tool-icon.tsx";
import AIToolTitle from "./ai-tool-title.tsx";
import {Layout, type Tool} from "./ai-tools-list.ts";
import AutosaveIndicator from "./autosave-indicator.tsx";
import {
    ADMIN_KHANMIGO_TOOLS_PATH,
    TEACHER_KHANMIGO_TOOLS_PATH,
} from "./path-helpers.ts";
import {Platform} from "./utils.ts";

import caretLeftIcon from "@phosphor-icons/core/regular/caret-left.svg";
import copySimpleIcon from "@phosphor-icons/core/regular/copy-simple.svg";

type Props = {
    handleMakeCopyClicked?: () => void;
    tool: Tool | AIContentTabTool;
    onSelectPreferences: () => void;
    resetDocumentAndChat: () => void;
    savingStatus: AutosavingStatus;
    platform: Platform;
    viewOnly?: boolean;
    isProvidingInputs?: boolean;
    showMakeCopyButton?: boolean;
    backButtonHref?: string;
    handleBlooketRedirect?: (redirectURL: string) => void;
    questionSetContent?: QuizContent;
    removeThreeDotMenu?: boolean;
    isPNGIcon?: boolean;
};

export default function AIToolHeader(props: Props) {
    const {
        handleMakeCopyClicked,
        tool,
        onSelectPreferences,
        resetDocumentAndChat,
        viewOnly,
        savingStatus,
        isProvidingInputs,
        platform,
        showMakeCopyButton,
        handleBlooketRedirect,
        questionSetContent,
        removeThreeDotMenu,
        isPNGIcon,
    } = props;
    const referralFlag = useWhamBoostReferralFlag();

    const teacherIsEligibleForReferrals = useTeacherIsEligibleForReferrals();

    const [referralModalOpen, setReferralModalOpen] = useState(false);

    const profileResult = useProfile();
    const {documentTitle, editor, readOnly} = useCoeditorContext();
    const navigation = useGuideState("navigation");
    const conversation = useGuideState("conversation");
    const threadId = useGuideState("threadId");
    const isHighTraffic = useGuideState("isHighTraffic");
    const remainingQuota = useGuideState("remainingQuota");
    const {href} = useLocation();
    const updateBlooket = useUpdateBlooketMutation();
    const exportQuestionSetToBlooket = useExportQuestionSetToBlooket();

    const [showMenu, setShowMenu] = useState(false);

    const persona = useAIGuideToolPersona();

    const handlePrintClicked = useCallback(
        (useExportEvent: boolean) => {
            if (!editor) {
                return;
            }
            clearUnsavedChanges(editor);

            if (useExportEvent) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportAsPDFSelected",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
            } else {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorPrintClicked",
                    eventSchemaVersion: 2,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                    currentPageURL: href,
                });
            }

            const editorDOMElement = ReactEditor.toDOMNode(editor, editor);
            const title = getDocumentTitle(editor);

            printContents(editorDOMElement, title, isIFrame());
            setReferralModalOpen(true);
        },
        [editor, navigation.id, href, threadId],
    );

    const threadHistoryEnabled = conversation.length > 1;
    const threadHistoryURL = `/profile/me/khanmigo/history/${threadId ?? ""}`;
    const showDevControls =
        (profileResult.status === "success" &&
            profileResult.profile?.get("hasAccessToAIGuideDev")) ||
        false;

    let backButtonBehavior;
    if (props.backButtonHref) {
        backButtonBehavior = {
            href: props.backButtonHref,
        };
    } else if (isProvidingInputs) {
        const khanmigoToolsPath =
            persona === "ADMIN"
                ? ADMIN_KHANMIGO_TOOLS_PATH
                : persona === "PARENT"
                  ? PARENT_KHANMIGO_TOOLS_PATH
                  : TEACHER_KHANMIGO_TOOLS_PATH;
        backButtonBehavior = {
            href:
                platform === Platform.Canvas
                    ? CANVAS_INTEGRATION_PATH
                    : khanmigoToolsPath,
        };
    } else {
        backButtonBehavior = {
            onClick: resetDocumentAndChat,
        };
    }

    const externalButtonDisabled = !editor || readOnly || isProvidingInputs;
    const handleExportToBlooket = () => {
        if (threadId && questionSetContent) {
            updateBlooket({
                threadId: threadId,
                title: questionSetContent.title,
                description: questionSetContent.description,
                questions: mapBlooketQuestionsToSavableFormat(
                    questionSetContent.questions,
                ),
            })
                .then(() => {
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideBlooketQuestionSetExportInitiated",
                        eventSchemaVersion: 1,
                        aiGuideThreadId: threadId ?? "",
                        numberOfQuestions: new IntegerField(
                            questionSetContent.questions.length,
                        ),
                    });

                    exportQuestionSetToBlooket({threadId}).then(
                        (redirectURL) => {
                            if (redirectURL && handleBlooketRedirect) {
                                handleBlooketRedirect(redirectURL);
                            }
                        },
                    );
                })
                .catch((error) => {
                    throw new KAError(
                        "Failed to export to Blooket",
                        Errors.Internal,
                        {
                            cause: error,
                        },
                    );
                });
        }
    };

    const shareButton = !viewOnly && (
        <ModalLauncher
            modal={({closeModal}) => (
                <GetShareLinkModal
                    title={documentTitle}
                    closeModal={closeModal}
                    documentID={threadId ?? ""}
                    onCopyLinkClick={() => {
                        publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideCoeditorLinkCopied",
                            eventSchemaVersion: 1,
                            documentId: threadId ?? "",
                            aiGuideExperienceReportingLabelId:
                                tool.reportingLabelId,
                        });
                    }}
                    persona={persona}
                />
            )}
        >
            {({openModal}) => (
                <Button
                    kind="tertiary"
                    style={styles.button}
                    disabled={externalButtonDisabled}
                    onClick={() => {
                        openModal();
                        publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideCoeditorShareSelected",
                            eventSchemaVersion: 1,
                            documentId: threadId ?? "",
                            aiGuideExperienceReportingLabelId:
                                tool.reportingLabelId,
                        });
                    }}
                >
                    {t`Share`}
                </Button>
            )}
        </ModalLauncher>
    );

    return (
        <>
            <View style={styles.container}>
                <View style={styles.header}>
                    {!viewOnly && (
                        <IconButton
                            kind="tertiary"
                            actionType="neutral"
                            icon={caretLeftIcon}
                            testId="back-arrow-button"
                            {...backButtonBehavior}
                        />
                    )}
                    <AIToolIcon
                        icon={tool.icon}
                        color={tool.color}
                        style={styles.icon}
                        isPNGIcon={isPNGIcon}
                    />
                    <AIToolTitle
                        toolTitle={tool.title}
                        threadId={threadId ?? ""}
                        navigationId={navigation.id}
                        viewOnly={viewOnly}
                    />
                    {(tool.layout === Layout.Coeditor ||
                        tool.layout === Layout.Blooket) &&
                        !isProvidingInputs &&
                        (viewOnly ? (
                            <Pill size="large">{t`View only`}</Pill>
                        ) : (
                            <AutosaveIndicator
                                status={savingStatus}
                                path={href}
                            />
                        ))}
                    <Spring />
                    {tool.layout === Layout.Blooket && !isProvidingInputs && (
                        <Button
                            disabled={
                                savingStatus !== "saved" ||
                                !isValidQuestionSet(questionSetContent)
                            }
                            onClick={handleExportToBlooket}
                            testId="export-to-blooket-button"
                        >
                            {t`Export to Blooket`}
                        </Button>
                    )}
                    {tool.layout === Layout.Coeditor && (
                        <>
                            {viewOnly &&
                                showMakeCopyButton &&
                                (handleMakeCopyClicked === undefined ? (
                                    <Tooltip
                                        content={t`Sorry, you can't make an editable copy of this document.`}
                                    >
                                        <Button
                                            kind="tertiary"
                                            startIcon={copySimpleIcon}
                                            disabled={true}
                                        >
                                            {t`Make a copy`}
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        kind="tertiary"
                                        startIcon={copySimpleIcon}
                                        onClick={() => handleMakeCopyClicked()}
                                    >
                                        {t`Make a copy`}
                                    </Button>
                                ))}
                            {shareButton}
                            <Button
                                kind="tertiary"
                                style={styles.button}
                                disabled={externalButtonDisabled}
                                onClick={() => handlePrintClicked(false)}
                            >
                                {t`Print`}
                            </Button>
                            <AIToolExportDropdown
                                setReferralModalOpen={setReferralModalOpen}
                                isProvidingInputs={isProvidingInputs}
                                tool={tool}
                            />
                        </>
                    )}
                    <Strut size={spacing.medium_16} />
                    {!removeThreeDotMenu && !viewOnly && (
                        <>
                            <PowerIndicator
                                isHighTraffic={isHighTraffic}
                                remainingQuota={remainingQuota}
                                color={color.offBlack}
                                style={styles.powerIcon}
                            />
                            <Strut size={spacing.xSmall_8} />

                            <HeaderMenu
                                expanded
                                type={HeaderType.AITools}
                                menuOpen={showMenu}
                                onToggleMenu={() => setShowMenu(!showMenu)}
                                onResetChat={resetDocumentAndChat}
                                onSelectPreferences={onSelectPreferences}
                                showDevControls={showDevControls}
                                threadHistoryEnabled={threadHistoryEnabled}
                                onDownloadThread={() =>
                                    downloadConversation(threadId, conversation)
                                }
                                onOpenThreadHistory={() => {
                                    const newWindow = window.open(
                                        threadHistoryURL,
                                        "_blank",
                                    );
                                    if (newWindow) {
                                        newWindow.focus();
                                    }
                                }}
                            />
                        </>
                    )}
                </View>
            </View>
            {referralFlag && teacherIsEligibleForReferrals && (
                <Dismissible
                    dismissKey="teachersReferralInProductModal"
                    expiresDays="never"
                    showOnce={true}
                >
                    {(dismiss) => (
                        <KhanmigoForTeachersReferralInProductModal
                            isOpen={referralModalOpen}
                            onClose={dismiss}
                        />
                    )}
                </Dismissible>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flexShrink: 0,
        flexGrow: 0,
    },
    header: {
        padding: `${spacing.small_12}px ${spacing.xLarge_32}px `,
        backgroundColor: color.offWhite,
        flexShrink: 0,
        flexDirection: "row",
        alignItems: "center",
        borderBottom: `1px solid ${color.offBlack8}`,
        height: 72,
    },
    icon: {
        marginRight: spacing.xSmall_8,
        marginLeft: spacing.xSmall_8,
    },
    powerIcon: {
        justifyContent: "center",
    },
    button: {
        marginLeft: spacing.medium_16,
    },
});
